import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ScrapingJobService } from '@/services/scraping-job.service';
import { useAuth } from '@/contexts/AuthContext';

export const ScrapeRestaurants: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { profile } = useAuth();
  
  const [searchQuery, setSearchQuery] = useState('restaurant');
  const [location, setLocation] = useState('Minnesota');
  const [source, setSource] = useState<'google_maps' | 'yelp' | 'both'>('both');
  const [filters, setFilters] = useState({
    minReviews: 4,
    maxLocations: 3,
    excludeChains: true,
    priceTier: [] as string[],
  });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: async () => {
      console.log('[ScrapeRestaurants] Starting scraping job creation...', {
        searchQuery,
        location,
        source,
        filters,
        userId: profile?.id,
      });

      if (!profile?.id) {
        const error = new Error('User not authenticated');
        console.error('[ScrapeRestaurants] Authentication error:', error);
        throw error;
      }
      
      let jobId: string;
      try {
        console.log('[ScrapeRestaurants] Creating scraping job in Firestore...');
        jobId = await ScrapingJobService.createScrapingJob({
          searchQuery,
          location,
          source,
          filters,
          createdBy: profile.id,
        });
        console.log('[ScrapeRestaurants] Scraping job created successfully:', { jobId });
      } catch (error: any) {
        console.error('[ScrapeRestaurants] Error creating scraping job:', {
          error,
          errorMessage: error?.message,
          stack: error?.stack,
        });
        throw new Error(`Failed to create scraping job: ${error?.message || 'Unknown error'}`);
      }

      // Trigger the scraping job
      try {
        console.log('[ScrapeRestaurants] Triggering scraping job via Cloud Function...', { jobId });
        await ScrapingJobService.triggerScraping(jobId, searchQuery, location, source, filters);
        console.log('[ScrapeRestaurants] Scraping job triggered successfully:', { jobId });
      } catch (error: any) {
        console.error('[ScrapeRestaurants] Error triggering scraping job:', {
          jobId,
          error,
          errorMessage: error?.message,
          errorCode: error?.code,
          originalError: (error as any)?.originalError,
          stack: error?.stack,
        });
        
        // Re-throw with more context
        const errorMessage = error?.message || 'Unknown error occurred';
        throw new Error(`Failed to start scraping: ${errorMessage}`);
      }
      
      return jobId;
    },
    onSuccess: (jobId) => {
      console.log('[ScrapeRestaurants] Mutation successful:', { jobId });
      queryClient.invalidateQueries({ queryKey: ['scrapingJobs'] });
      alert(`Scraping job started! Job ID: ${jobId}\n\nResults will appear in the approval queue when complete.`);
      navigate('/sales/scraping-jobs');
    },
    onError: (error: any) => {
      console.error('[ScrapeRestaurants] Mutation error:', {
        error,
        errorMessage: error?.message,
        errorName: error?.name,
        stack: error?.stack,
        originalError: error?.originalError,
      });
      
      // Show user-friendly error message
      const message = error?.message || 'An unknown error occurred while starting the scraping job.';
      setErrorMessage(message);
      
      // Also show alert as fallback
      alert(`Error: ${message}\n\nPlease check the browser console (F12) for detailed logs.`);
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null); // Clear any previous errors
    mutation.mutate();
  };

  const handlePriceTierChange = (tier: string, checked: boolean) => {
    if (checked) {
      setFilters({ ...filters, priceTier: [...filters.priceTier, tier] });
    } else {
      setFilters({ ...filters, priceTier: filters.priceTier.filter(t => t !== tier) });
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <button
          onClick={() => navigate('/sales')}
          className="text-blue-600 hover:text-blue-700 text-sm mb-4"
        >
          ← Back to Sales
        </button>
        <h1 className="text-2xl font-bold text-gray-900">Scrape Restaurant Data</h1>
        <p className="text-gray-600 mt-1">Automatically scrape restaurants from Google Maps, Yelp, and other sources</p>
      </div>

      <form onSubmit={handleSubmit} className="bg-white shadow rounded-lg p-6 space-y-6">
        {/* Error Message */}
        {errorMessage && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <div className="mt-2 text-sm text-red-700">
                  <p>{errorMessage}</p>
                  <p className="mt-2 text-xs text-red-600">
                    Check the browser console (F12) for detailed error logs.
                  </p>
                </div>
                <div className="mt-4">
                  <button
                    type="button"
                    onClick={() => setErrorMessage(null)}
                    className="text-sm font-medium text-red-800 hover:text-red-900"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Search Configuration */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Search Configuration</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Search Query *
              </label>
              <input
                type="text"
                required
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="restaurant, bar, cafe, etc."
              />
              <p className="mt-1 text-xs text-gray-500">
                What type of business to search for
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Location *
              </label>
              <input
                type="text"
                required
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Minnesota, Minneapolis, etc."
              />
              <p className="mt-1 text-xs text-gray-500">
                City, state, or region to search
              </p>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Data Sources *
              </label>
              <div className="flex space-x-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="google_maps"
                    checked={source === 'google_maps'}
                    onChange={(e) => setSource(e.target.value as 'google_maps' | 'yelp' | 'both')}
                    className="mr-2"
                  />
                  Google Maps
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="yelp"
                    checked={source === 'both'}
                    onChange={(e) => setSource(e.target.value as any)}
                    className="mr-2"
                  />
                  Yelp
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="both"
                    checked={source === 'both'}
                    onChange={(e) => setSource(e.target.value as any)}
                    className="mr-2"
                  />
                  Both (Recommended)
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="border-t pt-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Filters</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Minimum Reviews
              </label>
              <input
                type="number"
                min="0"
                value={filters.minReviews}
                onChange={(e) => setFilters({ ...filters, minReviews: parseInt(e.target.value) || 0 })}
                className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="mt-1 text-xs text-gray-500">
                Only include businesses with at least this many reviews
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Maximum Locations
              </label>
              <input
                type="number"
                min="1"
                value={filters.maxLocations}
                onChange={(e) => setFilters({ ...filters, maxLocations: parseInt(e.target.value) || 3 })}
                className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="mt-1 text-xs text-gray-500">
                Focus on independent restaurants (1-3 locations)
              </p>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                checked={filters.excludeChains}
                onChange={(e) => setFilters({ ...filters, excludeChains: e.target.checked })}
                className="mr-2"
              />
              <label className="text-sm font-medium text-gray-700">
                Exclude known chains
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Price Tier (Optional)
              </label>
              <div className="flex space-x-4">
                {['$', '$$', '$$$', '$$$$'].map((tier) => (
                  <label key={tier} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={filters.priceTier.includes(tier)}
                      onChange={(e) => handlePriceTierChange(tier, e.target.checked)}
                      className="mr-1"
                    />
                    <span className="text-sm">{tier}</span>
                  </label>
                ))}
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Leave empty to include all price tiers
              </p>
            </div>
          </div>
        </div>

        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
          <p className="text-sm text-blue-800">
            <strong>How it works:</strong> This will search {source === 'both' ? 'Google Maps and Yelp' : source} for "{searchQuery}" in "{location}". 
            Results will be automatically scraped and added to the approval queue for review.
          </p>
        </div>

        {/* Submit */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/sales')}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={mutation.isPending}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
          >
            {mutation.isPending ? 'Starting Scraping Job...' : 'Start Scraping'}
          </button>
        </div>
      </form>

      {/* Example Searches */}
      <div className="mt-8 bg-gray-50 border border-gray-200 rounded-lg p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Example Searches</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <p className="font-medium text-gray-700 mb-2">Restaurants in Minnesota:</p>
            <ul className="list-disc list-inside text-gray-600 space-y-1">
              <li>Search: "restaurant"</li>
              <li>Location: "Minnesota"</li>
              <li>Min Reviews: 4</li>
              <li>Max Locations: 3</li>
            </ul>
          </div>
          <div>
            <p className="font-medium text-gray-700 mb-2">Bars in Minneapolis:</p>
            <ul className="list-disc list-inside text-gray-600 space-y-1">
              <li>Search: "bar"</li>
              <li>Location: "Minneapolis, MN"</li>
              <li>Min Reviews: 10</li>
              <li>Max Locations: 2</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};
